import { Button, EmptyState } from '@dev-spendesk/grapes';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { BetaFeatureNotice } from 'src/core/common/components/BetaFeatureNotice';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { useCostCentersQuery } from 'src/core/modules/budgets/apis';
import { useGetBudgetDefinitions } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useGetBudgetDefinitions';
import { type FilterSettings } from 'src/core/modules/budgets/models/budgetFilters';
import { type BudgetaryExercise } from 'src/core/modules/budgets/models/budgetaryExercise';
import { doesPeriodPassFilters } from 'src/core/modules/budgets/utils/budgetFilters';
import {
  compareBudgetsV1ByStatus,
  compareBudgetsV2ByStatus,
} from 'src/core/modules/budgets/utils/compareBudgetsByStatus';
import {
  getHighlightedBudgetId,
  useScrollToHighlightedBudgetId,
} from 'src/core/modules/budgets/utils/useScrollToHighlightedBudgetId';

import { FilterPanel } from './FilterPanel';
import { useBudgetaryExercisesQuery } from '../../../../containers/hooks/useBudgetaryExercisesQuery';
import { sortNamedItems } from '../../../../utils/sortNamedItems';
import { BudgetaryExercisesPageSkeleton } from '../../v1/NewBudgetaryExercisesPage/BudgetaryExercisesPageSkeleton';
import { BudgetCardV2 } from '../BudgetCardV2';
import { BudgetaryExerciseCardV2 } from '../BudgetaryExerciseCardV2';
import { usePeriodRanges } from '../hooks/usePeriodRanges';

export const BudgetsPageV2 = () => {
  const companyId = useCompanyId();
  const history = useHistory();
  const { state, search } = useLocation();
  const { t, activeLanguage } = useTranslation('global');
  const { isAccountOwner, isController } = useUserRoles();
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    status: undefined,
    period: { start: undefined, end: undefined },
  });

  const budgetDefinitionsQuery = useGetBudgetDefinitions();
  const budgetDefinitions = budgetDefinitionsQuery.data ?? [];
  const highlightedBudgetId = getHighlightedBudgetId(search, state);
  useScrollToHighlightedBudgetId(highlightedBudgetId);

  useEffect(() => {
    if (highlightedBudgetId && budgetDefinitionsQuery.isSuccess) {
      const timer = setTimeout(() => {
        const element = document.getElementById(
          `budget-${highlightedBudgetId}`,
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [highlightedBudgetId, budgetDefinitionsQuery.isSuccess]);

  const budgetaryExercisesQuery = useBudgetaryExercisesQuery();
  const budgetaryExercisesRaw =
    budgetaryExercisesQuery.status === 'success'
      ? budgetaryExercisesQuery.data
      : [];

  const costCentersQuery = useCostCentersQuery({ withDeleted: true });
  const costCenters =
    costCentersQuery.status === 'success' ? costCentersQuery.data : [];

  // Enrich budgetary exercises with cost center names
  const budgetaryExercises = useMemo(() => {
    if (
      budgetaryExercisesQuery.status !== 'success' ||
      costCentersQuery.status !== 'success'
    ) {
      return [];
    }

    return budgetaryExercisesRaw.map(
      (budgetaryExercise: BudgetaryExercise) => ({
        ...budgetaryExercise,
        budgets: budgetaryExercise.budgets.map((budget) => {
          const costCenter = costCenters.find(
            (cc) => cc.id === budget.costCenterId,
          );
          return {
            ...budget,
            name: costCenter?.name ?? '',
            isUnavailable: costCenter?.isDeleted,
          };
        }),
      }),
    );
  }, [
    budgetaryExercisesQuery.status,
    budgetaryExercisesRaw,
    costCentersQuery.status,
    costCenters,
  ]);

  const selectedPeriodRanges = usePeriodRanges();
  const canEditBudgets = isController || isAccountOwner;

  const handleRowClick = (budgetId: string, budgetRowId: string) => {
    const budgetsPageRoute = routeFor(routes.BUDGET_V2_OVERVIEW.path, {
      id: budgetId,
      company: companyId,
    });
    history.push({
      pathname: budgetsPageRoute,
      state: {
        hasBackButton: true,
        ...selectedPeriodRanges.getLocationState(),
        budgetRowId,
      },
    });
  };

  const handleV1RowClick = (budgetId: string, expenseCategoryId?: string) => {
    const budgetsPageRoute = expenseCategoryId
      ? routeFor(routes.SUB_BUDGET_OVERVIEW.path, {
          id: budgetId,
          subBudgetId: expenseCategoryId,
          company: companyId,
        })
      : routeFor(routes.BUDGET_OVERVIEW.path, {
          id: budgetId,
          company: companyId,
        });
    history.push({
      pathname: budgetsPageRoute,
      state: {
        hasBackButton: true,
      },
    });
  };

  const filteredV2Budgets = budgetDefinitions.filter((budget) =>
    doesPeriodPassFilters(budget.period, filterSettings),
  );

  const v2Budgets = sortNamedItems(filteredV2Budgets, activeLanguage)
    .sort(compareBudgetsV2ByStatus)
    .map((definition) => (
      <div key={definition.id} id={`budget-${definition.id}`}>
        <BudgetCardV2
          budgetDefinition={definition}
          selectedPeriodRange={selectedPeriodRanges.getById(definition.id)}
          onSelectedPeriodRangeChange={(period) =>
            selectedPeriodRanges.setById(definition.id, period)
          }
          onRowClick={(rowId) => handleRowClick(definition.id, rowId)}
        />
      </div>
    ));

  const filteredV1Budgets = budgetaryExercises.filter((budget) =>
    doesPeriodPassFilters(
      { start: budget.startDate, end: budget.endDate },
      filterSettings,
    ),
  );

  const v1Budgets = sortNamedItems(filteredV1Budgets, activeLanguage)
    .sort(compareBudgetsV1ByStatus)
    .map((exercise) => (
      <div key={exercise.id} id={`budget-${exercise.id}`}>
        <BudgetaryExerciseCardV2
          budgetaryExercise={exercise}
          canEditBudgets={canEditBudgets}
          activeLanguage={activeLanguage}
          onRowClick={handleV1RowClick}
        />
      </div>
    ));

  // Early return for loading state - must come after all hooks
  if (
    budgetDefinitionsQuery.isLoading ||
    budgetaryExercisesQuery.status === 'loading'
  ) {
    return <BudgetaryExercisesPageSkeleton />;
  }

  const hasAnyBudgets =
    budgetDefinitions.length > 0 || budgetaryExercises.length > 0;

  const activeFiltersCount = [
    filterSettings.status,
    filterSettings.period.start,
    filterSettings.period.end,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFiltersCount !== 0;

  return (
    <ErrorBoundary context={{ scope: 'budgets-v2', team: 'control' }}>
      <div className="page__container flex flex-col gap-32 pb-40">
        <HeaderWithNavigation
          links={[]}
          rightContent={
            <div className="flex items-center gap-16">
              <BetaFeatureNotice
                featureName={FEATURES.BUDGETS_BY_DIMENSIONS}
                feedbackTags={['budgets-v2']}
                showOnlyFeedbackModal
              />
              <Button
                iconName="adjustments-horizontal"
                text={
                  hasActiveFilters
                    ? `${t('misc.filters')} (${activeFiltersCount})`
                    : t('misc.filters')
                }
                variant={
                  hasActiveFilters ? 'secondaryBrand' : 'secondaryNeutral'
                }
                onClick={() => {
                  setIsFiltersPanelOpen(true);
                }}
              />
              <Button
                text={t('budget.redesigned.createBudgetByDimensions')}
                onClick={() => {
                  history.push(
                    routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
                      company: companyId,
                    }),
                  );
                }}
              />
            </div>
          }
        >
          {t('navigation.budgets')}
        </HeaderWithNavigation>
        {!hasAnyBudgets ? (
          <EmptyState
            title={t('budget.redesigned.emptyState.title')}
            subtitle={t('budget.redesigned.emptyState.text')}
            actions={
              <Button
                text={t('budget.redesigned.createBudgetByDimensions')}
                onClick={() => {
                  history.push(
                    routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
                      company: companyId,
                    }),
                  );
                }}
              />
            }
            iconName="circle-information"
          />
        ) : null}
        {hasAnyBudgets &&
        hasActiveFilters &&
        filteredV1Budgets.length === 0 &&
        filteredV2Budgets.length === 0 ? (
          <EmptyState
            title={t('budget.redesigned.filters.noSearchResults')}
            subtitle={t('budget.redesigned.filters.noSearchResultsDescription')}
            iconName="circle-information"
          />
        ) : null}
        <div className="flex w-full flex-col gap-24 self-center p-40 pt-0">
          {/* Budget V2 cards */}
          {v2Budgets}

          {/* Budget V1 cards */}
          {v1Budgets}
        </div>
      </div>
      {isFiltersPanelOpen && (
        <FilterPanel
          onClose={() => setIsFiltersPanelOpen(false)}
          onChangeFilterSetting={(filter) => {
            setFilterSettings(filter);
          }}
          filterSetting={filterSettings}
        />
      )}
    </ErrorBoundary>
  );
};
